import { useCallback, useEffect, useState } from "react";

// Domínio de acesso da organização (white-label) - o cliente configura sozinho:
//  - subdomínio da plataforma (ex.: install.assistant.mocsolucoes.com.br),
//    validado e único, sem precisar mexer em DNS;
//  - domínio próprio, com prova de posse via registro TXT + CNAME.
// Rotas: /api/admin/org/domains* (admin da org).

interface PendingDomain {
  domain: string;
  record: string;
  value: string;
}

interface DomainsState {
  platformBase: string;
  domains: string[];
  subdomain: string | null;
  pending: PendingDomain[];
}

interface Props {
  /** Sysadmin só - gerencia a org escolhida no seletor. */
  orgId?: string;
}

export function OrgDomainsPanel({ orgId }: Props) {
  const qs = orgId ? `?orgId=${encodeURIComponent(orgId)}` : "";
  const [state, setState] = useState<DomainsState | null>(null);
  const [slug, setSlug] = useState("");
  const [ownDomain, setOwnDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await fetch(`/api/admin/org/domains${qs}`);
    if (r.ok) {
      const j = (await r.json()) as DomainsState;
      setState(j);
      setSlug(j.subdomain ? j.subdomain.replace(`.${j.platformBase}`, "") : "");
    }
  }, [qs]);

  useEffect(() => {
    void load();
  }, [load]);

  const call = useCallback(
    async (path: string, init: RequestInit, successMsg: string | null) => {
      setBusy(true);
      setError(null);
      setOkMsg(null);
      try {
        const r = await fetch(`${path}${qs}`, init);
        const j = await r.json().catch(() => ({}));
        if (!r.ok) {
          setError((j as { error?: string }).error ?? "Falha na operação.");
          return false;
        }
        setState(j as DomainsState);
        if (successMsg) setOkMsg(successMsg);
        return true;
      } finally {
        setBusy(false);
      }
    },
    [qs],
  );

  if (!state) return null;

  const activeOwnDomains = state.domains.filter((d) => !d.endsWith(`.${state.platformBase}`));

  return (
    <div className="admin-block">
      <h2>Domínio de acesso</h2>
      <p className="admin-sub">
        Endereço pelo qual sua equipe acessa o assistente com a sua marca (logo, cores e nome próprios).
      </p>
      {error && <div className="auth-expired" style={{ marginBottom: 10 }}>{error}</div>}
      {okMsg && !error && (
        <div className="admin-empty small" style={{ color: "var(--success, #2a9d5c)", marginBottom: 10 }}>{okMsg}</div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label htmlFor="dom-slug" style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
          Subdomínio na plataforma
        </label>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            id="dom-slug"
            type="text"
            value={slug}
            placeholder="suaempresa"
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            style={{ flex: "0 1 180px", minWidth: 0, padding: "6px 8px", fontSize: 13 }}
          />
          <span className="admin-user-meta">.{state.platformBase}</span>
          <button
            className="btn-approve"
            type="button"
            disabled={busy || !slug.trim() || `${slug.trim()}.${state.platformBase}` === state.subdomain}
            onClick={() =>
              void call(
                "/api/admin/org/domains/subdomain",
                {
                  method: "POST",
                  headers: { "content-type": "application/json" },
                  body: JSON.stringify({ slug: slug.trim() }),
                },
                "Subdomínio ativado - já pode acessar por ele.",
              )
            }
          >
            {state.subdomain ? "Trocar" : "Ativar"}
          </button>
        </div>
        {state.subdomain && (
          <p className="admin-user-meta" style={{ marginTop: 6 }}>
            Ativo: <a href={`https://${state.subdomain}`} target="_blank" rel="noreferrer">{state.subdomain}</a>
          </p>
        )}
      </div>

      <div>
        <label htmlFor="dom-own" style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
          Domínio próprio
        </label>
        <p className="admin-user-meta" style={{ marginBottom: 6 }}>
          Use um endereço seu (ex.: assistente.suaempresa.com.br). Exige comprovar a posse por um
          registro TXT e apontar um CNAME para <code>{state.platformBase}</code>.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            id="dom-own"
            type="text"
            value={ownDomain}
            placeholder="assistente.suaempresa.com.br"
            onChange={(e) => setOwnDomain(e.target.value.toLowerCase())}
            style={{ flex: "1 1 240px", minWidth: 0, padding: "6px 8px", fontSize: 13 }}
          />
          <button
            className="btn-approve"
            type="button"
            disabled={busy || !ownDomain.trim()}
            onClick={() =>
              void call(
                "/api/admin/org/domains/request",
                {
                  method: "POST",
                  headers: { "content-type": "application/json" },
                  body: JSON.stringify({ domain: ownDomain.trim() }),
                },
                null,
              ).then((ok) => {
                if (ok) setOwnDomain("");
              })
            }
          >
            Adicionar
          </button>
        </div>

        {state.pending.map((p) => (
          <div key={p.domain} className="admin-user" style={{ marginTop: 10, display: "block" }}>
            <div className="admin-user-info" style={{ marginBottom: 6 }}>
              <span className="admin-user-name">{p.domain}</span>
              <span className="admin-user-meta">aguardando verificação de posse</span>
            </div>
            <p className="admin-user-meta" style={{ marginBottom: 4 }}>
              Crie este registro <strong>TXT</strong> no DNS do domínio e clique em verificar:
            </p>
            <pre style={{ fontSize: 12, padding: "6px 8px", overflowX: "auto", background: "var(--bg-elevated)", borderRadius: 6 }}>
              {p.record}  TXT  "{p.value}"
            </pre>
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <button
                className="btn-approve"
                type="button"
                disabled={busy}
                onClick={() =>
                  void call(
                    "/api/admin/org/domains/verify",
                    {
                      method: "POST",
                      headers: { "content-type": "application/json" },
                      body: JSON.stringify({ domain: p.domain }),
                    },
                    "Domínio verificado e ativado. Falta só o CNAME (se ainda não fez).",
                  )
                }
              >
                Verificar agora
              </button>
              <button
                className="btn-reject"
                type="button"
                disabled={busy}
                onClick={() =>
                  void call(`/api/admin/org/domains?domain=${encodeURIComponent(p.domain)}`, { method: "DELETE" }, null)
                }
              >
                Cancelar
              </button>
            </div>
          </div>
        ))}

        {activeOwnDomains.length > 0 && (
          <div style={{ marginTop: 10 }}>
            {activeOwnDomains.map((d) => (
              <div key={d} className="admin-user">
                <div className="admin-user-info">
                  <span className="admin-user-name">{d}</span>
                  <span className="admin-user-meta">ativo</span>
                </div>
                <button
                  className="btn-reject"
                  type="button"
                  disabled={busy}
                  onClick={() => void call(`/api/admin/org/domains?domain=${encodeURIComponent(d)}`, { method: "DELETE" }, null)}
                >
                  Remover
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
